import React, { PureComponent } from "react"
import { select } from "d3-selection"
import d3Tip from "d3-tip"

export default class InsightAreasCoverageGraph extends PureComponent {
    constructor(props) {
        super(props)
        this.state = {}
        this.margin = {
            top: 10,
            bottom: 10,
            left: 10,
            right: 10,
        }
        this.minSquareSize = 40
        this.squareSpacingFactor = 1.2
        this.createGraph = this.createGraph.bind(this)
        this.windowResize = this.windowResize.bind(this)
    }

    componentDidMount() {
        this.setState({
            width: this.calculateWidth(this.graphContainer, this.margin),
            containerHeight: this.getContainerHeight(
                this.graphContainer,
                this.header,
                this.margin
            ),
        })
        // Add windowHeight to state to force a re-render on window resizing
        window.addEventListener("resize", this.windowResize)
    }

    windowResize() {
        this.setState({
            windowHeight: window.innerHeight,
        })
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.windowResize)
    }

    createGraph(data, { height, squareSize, squarePlusSpace, squaresPerLine }) {
        select(this.graphContainer).select("svg").remove()
        const graph = select(this.graphContainer)
            .append("svg")
            .attr("width", "100%")
            .attr("height", height + this.margin.top + this.margin.bottom)
            .append("g")
            .attr(
                "transform",
                `translate(${this.margin.left}, ${this.margin.top})`
            )

        const barContainer = graph
            .selectAll(".square")
            .data(data)
            .enter()
            .append("g")

        const tip = d3Tip()
            .attr("class", "d3-tip")
            .offset([0, 0])
            .html(
                (coverageItem) => `${coverageItem.topic}: ${coverageItem.count}`
            )

        graph.call(tip)

        const divisor = data.reduce(
            (previousMax, current) => Math.max(previousMax, current.count),
            0
        )

        barContainer
            .append("rect")
            .classed("square", true)
            .attr("fill", "hsl(201, 66%, 100%)")
            .attr("width", squareSize)
            .attr("height", squareSize)
            .attr(
                "transform",
                (_, index) =>
                    `translate(${(index % squaresPerLine) * squarePlusSpace}, ${
                        Math.floor(index / squaresPerLine) * squarePlusSpace
                    })`
            )
            .on("mouseover", tip.show)
            .on("mouseout", tip.hide)
            .transition()
            .delay((_, index) => index * 2 + 1000)
            .duration(1000)
            .attr("fill", (coverageItem) => {
                const lightness = 100 - (coverageItem.count / divisor) * 50
                return `hsl(201, 66%, ${lightness}%)`
            })
    }

    calculateProperties(
        dataLength,
        container,
        margin,
        minSquareSize,
        squareSpacingFactor
    ) {
        const width = this.calculateWidth(container, margin)
        const containerHeight = this.getContainerHeight(
            container,
            this.header,
            margin
        )
        let squareSize = minSquareSize
        let squarePlusSpace = squareSize * squareSpacingFactor
        let squaresPerLine = Math.floor(width / squarePlusSpace)
        let height =
            squarePlusSpace * (dataLength / squaresPerLine) +
            squarePlusSpace / 2

        if (containerHeight > height) {
            //If there is more space, resize the squares inline with the increase in space
            squareSize *= Math.pow(containerHeight / height, 0.5)
            squarePlusSpace = squareSize * squareSpacingFactor
            squaresPerLine = Math.floor(width / squarePlusSpace)
            //Add an extra square if it can fit without the space
            squaresPerLine += width % squarePlusSpace >= squareSize ? 1 : 0
            height = containerHeight + squarePlusSpace / 2
        }

        return {
            width,
            height,
            squareSize,
            squarePlusSpace,
            squaresPerLine,
        }
    }

    calculateWidth(container, margin) {
        return container.clientWidth - margin.left - margin.right
    }

    getContainerHeight(container, header, margin) {
        // Factor of 0.95 is due to widget-content of 0.95
        return (
            (container.clientHeight - header.clientHeight) * 0.95 -
            margin.top -
            margin.bottom
        )
    }

    render() {
        if (this.props.coverage && this.state.containerHeight) {
            const properties = this.calculateProperties(
                this.props.coverage.length,
                this.graphContainer,
                this.margin,
                this.minSquareSize,
                this.squareSpacingFactor
            )
            this.createGraph(this.props.coverage, properties)
        }
        return (
            <div className="widget">
                <h3
                    className="widget-header"
                    ref={(header) => (this.header = header)}
                >
                    <span className="icon icon-insights" />
                    Insight areas coverage
                </h3>
                <div
                    className="widget-content"
                    ref={(container) => (this.graphContainer = container)}
                />
            </div>
        )
    }
}
